import React, { createContext, useCallback, useState, useContext } from "react";
import Swal from "sweetalert2";

import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { ref, set, get, child, remove } from "firebase/database";
import { auth, database } from "../config/firebase";
import { useAuth } from "./auth";

export interface DataTypeProfile {
  key: "uid";
  uid: string;
  name: string;
  turmas: number;
  status: string;
  type?: string;
  dataDeAdicao: string;
}

interface ProfileProps {
  uid?: string;
  name: string;
  telefone: string;
  password: string;
  institution: string;
  email: string;
  avatar: string;
}

interface ProfileContextData {
  Profiles: ProfileProps[];
  loadingProfiles: boolean;
  getAll(teacherUID: string): Promise<void>;
  createProfiles(ProfilesData: ProfileProps): Promise<void>;
  editProfiles(ProfilesData: ProfileProps): Promise<void>;
  getProfiles(): Promise<DataTypeProfile[]>;
  getStudents(): Promise<DataTypeProfile[]>;
  getUsers(): Promise<DataTypeProfile[]>;
  deleteProfile(uid: string): Promise<void>;
  toggleUserBlock(uid: string, isBlocked: boolean): Promise<void>;
  updateUserEmail(uid: string, newEmail: string): Promise<void>;
}

const ProfilesContext = createContext<ProfileContextData>(
  {} as ProfileContextData
);

const ProfilesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [ProfilessData, setProfiless] = useState([] as ProfileProps[]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  const deleteProfile = useCallback(async (uid: string) => {
    await remove(child(ref(database), `users/${uid}`));
  }, []);

  const getProfiles = useCallback(async () => {
    try {
      const resposta = await get(child(ref(database), "users"));

      const alunosList = [] as DataTypeProfile[];
      resposta.forEach((current) => {
        if (current.val().type !== "student" && current.val().type !== "user") {
          alunosList.push(current.val());
        }
      });
      return alunosList;
    } catch (error) {
      throw new Error("Houve um erro Inesperado");
    }
  }, []);

  const getStudents = useCallback(async () => {
    try {
      const resposta = await get(child(ref(database), "users"));
      const alunosList = [] as DataTypeProfile[];
      resposta.forEach((current) => {
        if (current.val().type === "student" || current.val().type === "user") {
          alunosList.push(current.val());
        }
      });
      return alunosList;
    } catch (error) {
      throw new Error("Houve um erro Inesperado");
    }
  }, []);

  const getUsers = useCallback(async () => {
    try {
      const resposta = await get(child(ref(database), "users"));
      const userList = [] as DataTypeProfile[];
      resposta.forEach((current) => {
        const val = current.val();
        userList.push({
          ...val,
          uid: current.key,
          key: current.key,
        });
      });
      return userList;
    } catch (error) {
      throw new Error("Houve um erro Inesperado ao buscar usuários");
    }
  }, []);

  const toggleUserBlock = useCallback(
    async (uid: string, isBlocked: boolean) => {
      try {
        await set(child(ref(database), `users/${uid}/isBlocked`), isBlocked);
      } catch (error) {
        throw new Error("Erro ao alterar status de bloqueio do usuário");
      }
    },
    []
  );

  const updateUserEmail = useCallback(async (uid: string, newEmail: string) => {
    try {
      await set(child(ref(database), `users/${uid}/email`), newEmail);
    } catch (error) {
      throw new Error("Erro ao atualizar e-mail do usuário no banco de dados");
    }
  }, []);

  const getAll = useCallback(async (uid: string) => {
    if (!uid) return;
    setLoadingProfiles(true);
    try {
      const snapshot = await get(child(ref(database), `profileTeste/${uid}`));
      const list: ProfileProps[] = [];
      snapshot.forEach((current) => {
        if (current.val()) list.push(current.val() as ProfileProps);
      });
      setProfiless(list);
    } catch (err) {
      console.error("Erro ao carregar profileTeste (permissão ou rede):", err);
      setProfiless([]);
    } finally {
      setLoadingProfiles(false);
    }
  }, []);

  const editProfiles = useCallback(
    async ({
      uid,
      name,
      email,
      telefone,
      institution,
      avatar,
    }: ProfileProps) => {
      setLoadingProfiles(true);

      Swal.fire({
        title: "Cadastrando...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();

          await set(child(ref(database), `users/${uid}`), {
            uid,
            name,
            email,
            telefone,
            institution,
            avatar,
          })
            .then(() => {
              Swal.close();
              Swal.fire({
                icon: "success",
                title: "Atualização de Profissional",
                text: "Profissional atualizado com sucesso!",
                showConfirmButton: false,
                timer: 1800,
              }).then(() => navigate("/profiles"));
            })
            .catch((err) => {
              switch (err.code) {
                default: {
                  Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: `Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.`,
                  });
                  break;
                }
              }
            })
            .finally(() => setLoadingProfiles(false));
        },
      });
    },
    [navigate]
  );

  const { user } = useAuth();
  const createProfiles = useCallback(
    async ({
      name,
      email,
      password,
      telefone,
      institution,
      avatar,
    }: ProfileProps) => {
      setLoadingProfiles(true);

      Swal.fire({
        title: "Cadastrando...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();
          const userUID = user?.uid;
          console.log("usuario", userUID);
          await createUserWithEmailAndPassword(auth, email, password)
            .then(async (res) => {
              const uid = res.user?.uid;
              await updateProfile(res.user, {
                displayName: name,
              });

              await set(child(ref(database), `users/${uid}`), {
                uid,
                name,
                email,
                telefone,
                institution,
                avatar,
                userUID,
              });
            })
            .then(() => {
              Swal.close();
              Swal.fire({
                icon: "success",
                title: "Cadastro de Profissional",
                text: "Profissional cadastrado com sucesso!",
                showConfirmButton: false,
                timer: 1800,
              }).then(() => navigate("/profiles"));
            })
            .catch((err) => {
              switch (err.code) {
                case "auth/email-already-in-use": {
                  Swal.fire({
                    denyButtonColor: "red",
                    confirmButtonColor: "green",
                    confirmButtonText: "Tentar novamente",
                    icon: "error",
                    title: "Oops...",
                    text: `Parece que o email informado já está em uso. \n\nEmail: ${email}`,
                  });
                  break;
                }
                default: {
                  Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: `Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.`,
                  });
                  break;
                }
              }
            })
            .finally(() => setLoadingProfiles(false));
        },
      });
    },
    [navigate, user?.uid]
  );
  const providerValue = React.useMemo(
    () => ({
      getProfiles,
      getStudents,
      getUsers,
      deleteProfile,
      toggleUserBlock,
      updateUserEmail,
      getAll,
      createProfiles,
      editProfiles,
      Profiles: ProfilessData,
      loadingProfiles,
    }),
    [
      getProfiles,
      getStudents,
      getUsers,
      deleteProfile,
      toggleUserBlock,
      updateUserEmail,
      getAll,
      createProfiles,
      editProfiles,
      ProfilessData,
      loadingProfiles,
    ]
  );

  return (
    <ProfilesContext.Provider value={providerValue}>
      {children}
    </ProfilesContext.Provider>
  );
};

function useProfiles(): ProfileContextData {
  const context = useContext(ProfilesContext);

  if (!context) {
    throw new Error("useProfiles must be used within an AuthProvider");
  }

  return context;
}

export { ProfilesProvider, useProfiles };
